// Invite landing — App Flow §1: validates before sign-in, joins after (architecture §3.8).
import { Button, Card, EmptyState, LogoMark } from '@roots/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { AuthScreen } from './auth.js';

export function JoinScreen() {
  const { token } = useParams({ strict: false }) as { token: string };
  const { user } = useAuth();
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.inviteInfo(token),
  });

  useEffect(() => {
    if (!user) sessionStorage.setItem('roots:invite', token);
  }, [user, token]);

  async function join() {
    setJoining(true);
    setError(null);
    try {
      const result = await api.joinByToken(token);
      sessionStorage.removeItem('roots:invite');
      void navigate({ to: '/families/$familyId', params: { familyId: result.family.id } });
    } catch {
      setError('We couldn’t add you to that family. Ask for a fresh link and try again.');
    } finally {
      setJoining(false);
    }
  }

  if (isLoading) {
    return (
      <main className="mx-auto max-w-[440px] p-6">
        <Card>
          <p className="type-body">Checking your invite…</p>
        </Card>
      </main>
    );
  }

  if (!data?.valid) {
    const message =
      data?.reason === 'expired'
        ? 'Invite links last 7 days. Ask your family member to send a new one.'
        : 'It might be incomplete or miscopied. Check with the person who sent it.';
    return (
      <main className="mx-auto max-w-[440px] p-6">
        <EmptyState
          message={message}
          actions={
            <Button variant="secondary" onClick={() => void navigate({ to: '/' })}>
              Create your own family
            </Button>
          }
        />
      </main>
    );
  }

  if (!user) return <AuthScreen />;

  return (
    <main className="mx-auto flex min-h-dvh max-w-[440px] flex-col justify-center gap-4 p-6">
      <Card className="flex flex-col gap-4 text-center">
        <LogoMark className="mx-auto h-20 w-auto text-[var(--color-accent-primary)]" />
        <h1 className="type-display">{data.family_name}</h1>
        <p className="type-body">
          You’ve been invited to join {data.family_name}
          {data.member_count ? ` — ${data.member_count} members so far` : ''}.
        </p>
        {error && <p className="type-caption text-[var(--color-error)]">{error}</p>}
        <Button onClick={join} loading={joining} fullWidth>
          Join this family
        </Button>
      </Card>
    </main>
  );
}
